// ─────────────────────────────────────────────────────────────
// Derive screening criteria from the protocol markdown — no LLM.
//
// Exclusions come from the "Hierarchy of Selection" table, in order,
// because the first criterion a record fails is its exclusion reason.
// Inclusions come from the PICO table's Include column, which is the
// AND-gate checklist a surviving record must satisfy.
// ─────────────────────────────────────────────────────────────

const EXCLUSION_CODE_BY_PHRASE = {
  // Current reviewer-style phrasing (matches what the protocol prompt now generates)
  "population out of scope": "E3: Population not of interest",
  "intervention out of scope": "E8: Intervention not of interest",
  "comparator out of scope": "E9: Comparator not of interest",
  "outcome not reported": "E4: Outcomes not of interest",
  "outcomes not reported": "E4: Outcomes not of interest",
  "study design out of scope": "E5: Study designs",
  "publication type not eligible": "E7: Publication",
  "outside eligible timeframe": "E10: Outside date range",
  "language not eligible": "E6: Language not of interest",
  "outside geographic scope": "E11: Setting or geography not of interest",
  "duplicate record": "E1: Duplicate",

};

// Fallback when the reason is phrased freely — match on the criterion name.
const EXCLUSION_CODE_BY_KEYWORD = [
  [/\bpopulation|patients?\b/i, "E3: Population not of interest"],
  [/\bintervention|treatment|therap/i, "E8: Intervention not of interest"],
  [/\bcomparator|control\b/i, "E9: Comparator not of interest"],
  [/\boutcomes?\b/i, "E4: Outcomes not of interest"],
  [/\bstudy design|design\b/i, "E5: Study designs"],
  [/\bpublication type|abstract only|conference\b/i, "E7: Publication"],
  [/\btime\s?frame|date range|publication year\b/i, "E10: Outside date range"],
  [/\blanguage\b/i, "E6: Language not of interest"],
  [/\bduplicate\b/i, "E1: Duplicate"],
  [/\banimal|in.?vitro|preclinical\b/i, "E2: Animal/In-vitro"],
  [/\bsetting|geograph|countr/i, "E11: Setting or geography not of interest"],
  [/\bfull.?text|retriev/i, "E12: Full text not retrievable"],
];

const INCLUSION_CODE_BY_PICO_ROW = {
  population: "I4: Population of interest",
  intervention: "I5: Intervention of interest",
  comparator: "I6: Comparator of interest",
  "study design": "I7: Study design of interest",
  "time frame": "I8: Timeframe of interest",
  timeframe: "I8: Timeframe of interest",
};

const isSeparatorRow = (line) => /^\|[\s|:-]+\|?$/.test(line.trim());

/** Split a markdown table row into trimmed cells. */
const splitRow = (line) =>
  line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());

/** Placeholder cells the AI or the blank template leaves behind. */
const isBlankCell = (v) => !v || /^(\.\.\.|-+|n\/?a|tbd|none)$/i.test(v);

/**
 * Pull the first markdown table that appears under a heading matching `re`.
 * Returns { header: string[], rows: string[][] } or null.
 */
const findTableUnderHeading = (markdown, re) => {
  const lines = markdown.replace(/\r\n?/g, "\n").split("\n");
  let i = lines.findIndex((l) => /^#{1,6}\s/.test(l) && re.test(l.replace(/^#+\s*/, "")));
  if (i === -1) return null;

  const table = [];
  for (i += 1; i < lines.length; i += 1) {
    const line = lines[i];
    if (/^#{1,6}\s/.test(line)) break;              // next heading — stop
    if (line.trim().startsWith("|")) table.push(line);
    else if (table.length) break;                    // table ended
  }
  if (table.length < 2) return null;

  const header = splitRow(table[0]).map((h) => h.toLowerCase());
  const rows = table.slice(1).filter((l) => !isSeparatorRow(l)).map(splitRow);
  return { header, rows };
};

/** Index of the first header cell matching `re`, or -1. */
const col = (header, re) => header.findIndex((h) => re.test(h));

const stageFlags = (raw = "") => {
  const s = raw.toLowerCase();
  if (/full.?text/.test(s) && !/both|abstract/.test(s))
    return { abstract_screening: false, full_text_screening: true };
  if (/abstract/.test(s) && !/both|full/.test(s))
    return { abstract_screening: true, full_text_screening: false };
  return { abstract_screening: true, full_text_screening: true };
};

const exclusionCode = (reason, criterion) => {
  const key = reason.toLowerCase().replace(/[.\s]+$/, "");
  if (EXCLUSION_CODE_BY_PHRASE[key]) return EXCLUSION_CODE_BY_PHRASE[key];
  const haystack = `${criterion} ${reason}`;
  const hit = EXCLUSION_CODE_BY_KEYWORD.find(([re]) => re.test(haystack));
  return hit ? hit[1] : null;
};

/**
 * @param   {string} markdown  protocol markdown
 * @returns {{exclusions: object[], inclusions: object[]}} plain criteria objects
 */
export function parseProtocolToCriteria(markdown = "") {
  if (!markdown.trim()) return { exclusions: [], inclusions: [] };

  const exclusions = [];
  const inclusions = [];
  // Track reason text already added per type, so two Hierarchy/PICO rows
  // that land on the exact same short phrase (e.g. two distinct outcome
  // rows both reducing to "Outcome not reported") don't produce duplicate
  // entries — dedupe is case-insensitive on the trimmed text.
  const seenExclusionReasons = new Set();
  const seenInclusionReasons = new Set();

  // ── Exclusions ← Hierarchy of Selection (order preserved) ──
  const hierarchy = findTableUnderHeading(markdown, /hierarch/i);
  if (hierarchy) {
    const { header, rows } = hierarchy;
    const cCriterion = col(header, /criterion|criteria/);
    const cReason = col(header, /reason/);
    const cStage = col(header, /stage/);

    rows.forEach((cells) => {
      const criterion = cCriterion > -1 ? cells[cCriterion] || "" : "";
      const reason = cReason > -1 ? cells[cReason] || "" : "";
      if (isBlankCell(criterion) && isBlankCell(reason)) return;

      const text = !isBlankCell(reason) ? reason : criterion;
      const dedupeKey = text.trim().toLowerCase();
      if (seenExclusionReasons.has(dedupeKey)) return;
      seenExclusionReasons.add(dedupeKey);

      exclusions.push({
        type: "EXCLUSION",
        reason: text,
        reason_code: exclusionCode(reason, criterion),
        ...stageFlags(cStage > -1 ? cells[cStage] : ""),
      });
    });
  }

  // ── PICO table ← inclusions, plus exclusions if no hierarchy ──
  const pico = findTableUnderHeading(markdown, /pico/i);
  if (pico) {
    const { header, rows } = pico;
    const cName = col(header, /criteri/);
    const cInclude = col(header, /includ/);
    const cExclude = col(header, /exclud/);
    const usePicoExclusions = exclusions.length === 0;

    rows.forEach((cells) => {
      const name = cName > -1 ? cells[cName] || "" : "";
      if (isBlankCell(name)) return;
      const key = name.toLowerCase().replace(/s$/, "");

      const include = cInclude > -1 ? cells[cInclude] || "" : "";
      if (!isBlankCell(include)) {
        const includeText = `${name}: ${include}`;
        const includeDedupeKey = includeText.trim().toLowerCase();
        if (!seenInclusionReasons.has(includeDedupeKey)) {
          seenInclusionReasons.add(includeDedupeKey);
          inclusions.push({
            type: "INCLUSION",
            reason: includeText,
            reason_code: INCLUSION_CODE_BY_PICO_ROW[key] || null,
            abstract_screening: true,
            full_text_screening: true,
          });
        }
      }

      const exclude = cExclude > -1 ? cells[cExclude] || "" : "";
      if (usePicoExclusions && !isBlankCell(exclude)) {
        const excludeText = `${name}: ${exclude}`;
        const excludeDedupeKey = excludeText.trim().toLowerCase();
        if (!seenExclusionReasons.has(excludeDedupeKey)) {
          seenExclusionReasons.add(excludeDedupeKey);
          exclusions.push({
            type: "EXCLUSION",
            reason: excludeText,
            reason_code: exclusionCode(exclude, name),
            abstract_screening: true,
            full_text_screening: true,
          });
        }
      }
    });
  }

  return { exclusions, inclusions };
}